import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid2,
  Paper,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { formatDateWithoutTime } from "../../../../../../views/components/common/functions/formatDate";
import {
  ClientLogoDto,
  ClientLogoScope,
  ClientLogoUploadedBy,
  DAILY_TASKS_STAGE_TABS,
  DailyTasksDetailResult,
  DailyTasksSlaStatus,
  MOCKUP_ITERATION_STATUS_LABELS,
  MOCKUP_WORK_STATE_LABELS,
  MockupDetailDto,
  MockupIterationDto,
  MockupIterationStatus,
  MockupWorkState,
  currentMockupRound,
  groupMockupIterations,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../styles/modernListingPageSx";
import { useDailyTasksFieldPermissions } from "./useDailyTasksFieldPermissions";
import { filenameFromUrl, ImagePreviewFrame } from "./DailyTasksMedia";
import { dailyTasksChipSx, getDailyTasksChipColors } from "./dailyTasksChips";
import { DailyTasksEmptyState } from "./DailyTasksEmptyState";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

const LOGO_UPLOADED_BY_LABEL: Record<ClientLogoUploadedBy, string> = {
  client: "Client",
  admin: "Admin",
  unknown: "",
};

const LOGO_SCOPE_LABEL: Record<ClientLogoScope, string> = {
  product: "This product",
  order: "Shared on order",
};

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  return formatDateWithoutTime(value) || "-";
};

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(Number(hours))) return "-";
  const value = Math.round(Number(hours));
  if (value < 24) return `${value}h`;
  const days = Math.floor(value / 24);
  const rem = value % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
};

const formatSlaDays = (days: number | null | undefined): string => {
  if (days == null || !Number.isFinite(Number(days))) return "-";
  return `${Number(days)}d`;
};

const isUploadSlaClock = (
  state: DailyTasksDetailResult["mockup_work_state"],
): boolean => state === "needs_upload";

const isReworkSlaClock = (
  state: DailyTasksDetailResult["mockup_work_state"],
): boolean => state === "changes_requested";

const isApprovalSlaClock = (
  state: DailyTasksDetailResult["mockup_work_state"],
): boolean => state === "awaiting_approval";

const pipelineLabel = (task: string): string => {
  const match = DAILY_TASKS_STAGE_TABS.find(
    (tab) =>
      tab.id === task ||
      tab.id.replace(/_/g, "") === task.replace(/_/g, ""),
  );
  if (match) return match.label;
  return task.replace(/_/g, " ");
};

const slaTone = (
  theme: Theme,
  status: DailyTasksSlaStatus,
): { bg: string; fg: string; label: string } => {
  try {
    if (status === "bad") {
      return { ...getDailyTasksChipColors(theme, "bad"), label: "Overdue" };
    }
    if (status === "warn") {
      return { ...getDailyTasksChipColors(theme, "warn"), label: "Watch" };
    }
    if (status === "ok") {
      return { ...getDailyTasksChipColors(theme, "ok"), label: "On track" };
    }
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  } catch (error) {
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  }
};

const workStateTone = (
  theme: Theme,
  state: DailyTasksDetailResult["mockup_work_state"],
): { bg: string; fg: string } => {
  try {
    if (state === "changes_requested") {
      return getDailyTasksChipColors(theme, "warn");
    }
    if (state === "needs_upload") {
      return getDailyTasksChipColors(theme, "bad");
    }
    if (state === "awaiting_approval") {
      return getDailyTasksChipColors(theme, "info");
    }
    return getDailyTasksChipColors(theme, "ok");
  } catch (error) {
    return getDailyTasksChipColors(theme, "ok");
  }
};

const iterationTone = (
  theme: Theme,
  status: MockupIterationStatus,
): { bg: string; fg: string } => {
  try {
    if (status === "approved") return getDailyTasksChipColors(theme, "ok");
    if (status === "rejected") return getDailyTasksChipColors(theme, "bad");
    if (status === "changes_requested") {
      return getDailyTasksChipColors(theme, "warn");
    }
    return getDailyTasksChipColors(theme, "info");
  } catch (error) {
    return getDailyTasksChipColors(theme, "info");
  }
};

const StatusPill: React.FC<{ label: string; bg: string; fg: string }> = ({
  label,
  bg,
  fg,
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 1,
      py: 0.25,
      borderRadius: 1,
      bgcolor: bg,
      color: fg,
    }}
  >
    <CustomTypography variant="caption" fontWeight={600} sx={{ color: fg }}>
      {label}
    </CustomTypography>
  </Box>
);

const SectionCaption: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();
  return (
    <CustomTypography
      variant="caption"
      fontWeight={600}
      sx={{
        color: getModernPageColors(theme).subtitle,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        display: "block",
        mb: 1,
      }}
    >
      {children}
    </CustomTypography>
  );
};

const logoMetaLine = (logo: ClientLogoDto): string => {
  const who = LOGO_UPLOADED_BY_LABEL[logo.uploaded_by];
  const when = logo.created_date ? formatDate(logo.created_date) : "";
  return [who, when].filter(Boolean).join(" · ");
};

const LogoGrid: React.FC<{ logos: ClientLogoDto[] }> = ({ logos }) => {
  const theme = useTheme();
  const pageColors = getModernPageColors(theme);
  const colors = theme.palette.customColors;

  return (
    <Grid2 container spacing={1.5} columns={12}>
      {logos.map((logo) => {
        const meta = logoMetaLine(logo);
        return (
          <Grid2 key={`${logo.scope}-${logo.id}`} size={{ xs: 12, sm: 6 }}>
            <Box
              sx={{
                border: `1px solid ${pageColors.border}`,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: colors.white,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ImagePreviewFrame
                src={logo.image_url}
                alt="client logo"
                filename={filenameFromUrl(
                  logo.image_url,
                  `logo-${logo.id}.jpg`,
                )}
              />
              {meta ? (
                <Box sx={{ p: 1.25 }}>
                  <CustomTypography variant="body3" fontWeight={500}>
                    {meta}
                  </CustomTypography>
                </Box>
              ) : null}
            </Box>
          </Grid2>
        );
      })}
    </Grid2>
  );
};

const LogoScopeBlock: React.FC<{
  scope: ClientLogoScope;
  emptyCopy: string;
  logos: ClientLogoDto[];
}> = ({ scope, emptyCopy, logos }) => {
  const theme = useTheme();
  const pageColors = getModernPageColors(theme);

  return (
    <Box>
      <CustomTypography
        variant="body3"
        fontWeight={600}
        sx={{ color: pageColors.headerBg, mb: 1 }}
      >
        {LOGO_SCOPE_LABEL[scope]} ({logos.length})
      </CustomTypography>
      {logos.length === 0 ? (
        <DailyTasksEmptyState
          inPaper={false}
          icon={ImageOutlinedIcon}
          title={emptyCopy}
        />
      ) : (
        <LogoGrid logos={logos} />
      )}
    </Box>
  );
};

const StatTile: React.FC<{
  eyebrow: string;
  value: string;
  hint?: string;
  pill?: { label: string; bg: string; fg: string };
}> = ({ eyebrow, value, hint, pill }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  return (
    <Box sx={{ height: "100%" }}>
      <CustomTypography
        variant="caption"
        fontWeight={600}
        sx={{
          color: colors.greyTextColor,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {eyebrow}
      </CustomTypography>
      <CustomTypography
        variant="heading5"
        fontWeight={600}
        sx={{
          color: colors.navyText,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          mt: 0.5,
        }}
      >
        {value}
      </CustomTypography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          mt: 0.75,
        }}
      >
        {pill ? <StatusPill {...pill} /> : null}
        {hint ? (
          <CustomTypography variant="caption" sx={{ color: colors.greyTextColor }}>
            {hint}
          </CustomTypography>
        ) : null}
      </Box>
    </Box>
  );
};

const mockupDecisionLabel = (mockup: MockupDetailDto): string => {
  if (Boolean(mockup.approved)) {
    return `Approved by ${mockup.approver_name || "-"}`;
  }
  if (Boolean(mockup.changes_requested)) {
    return mockup.changes_requested_by_name
      ? `Rework · ${mockup.changes_requested_by_name}`
      : "Rework";
  }
  if (Boolean(mockup.is_rejected)) {
    return mockup.rejection_note
      ? `Rejected: ${mockup.rejection_note}`
      : "Rejected";
  }
  return "Awaiting approval";
};

const MockupCard: React.FC<{
  mockup: MockupDetailDto;
  alt: string;
}> = ({ mockup, alt }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const pageColors = getModernPageColors(theme);
  const changeCount = Number(mockup.image_url_change_count ?? 0);

  return (
    <Box
      sx={{
        border: `1px solid ${pageColors.border}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: colors.white,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ImagePreviewFrame
        src={mockup.image_url}
        alt={alt}
        filename={filenameFromUrl(mockup.image_url, `mockup-${mockup.id}.jpg`)}
      />
      <Box sx={{ p: 1.25 }}>
        <CustomTypography
          variant="body3"
          fontWeight={500}
          sx={{ color: pageColors.title }}
        >
          {mockup.uploaded_by || "Unknown"} · {formatDate(mockup.created_date)}
        </CustomTypography>
        <CustomTypography
          variant="body3"
          fontWeight={500}
          sx={{
            display: "block",
            mt: 0.35,
            color: pageColors.subtitle,
          }}
        >
          {mockupDecisionLabel(mockup)}
        </CustomTypography>
        {changeCount > 1 ? (
          <CustomTypography
            variant="caption"
            sx={{ color: pageColors.subtitle, display: "block", mt: 0.35 }}
          >
            Changed {changeCount - 1} time{changeCount - 1 === 1 ? "" : "s"}
          </CustomTypography>
        ) : null}
        {mockup.comments.map((comment) => (
          <CustomTypography
            key={comment.id}
            variant="body3"
            fontWeight={500}
            sx={{
              display: "block",
              mt: 0.5,
              color: colors.mutedSlateText,
            }}
          >
            {comment.admin_name || comment.user_name || "Comment"}:{" "}
            {comment.comment}
          </CustomTypography>
        ))}
      </Box>
    </Box>
  );
};

const resolveIterations = (
  detail: DailyTasksDetailResult,
): MockupIterationDto[] => {
  if (Array.isArray(detail.iterations)) {
    return detail.iterations;
  }
  return groupMockupIterations(detail.mockups);
};

interface MockupDrawerBodyProps {
  detail: DailyTasksDetailResult;
}

const MockupDrawerBody: React.FC<MockupDrawerBodyProps> = ({ detail }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const pageColors = getModernPageColors(theme);
  const { canViewDispatchDate } = useDailyTasksFieldPermissions();
  const sla = slaTone(theme, detail.sla_status);
  const work = workStateTone(theme, detail.mockup_work_state);
  const iterations = useMemo(() => resolveIterations(detail), [detail]);
  const currentMockups = useMemo(
    () => currentMockupRound(detail.mockups),
    [detail.mockups],
  );
  const newestFirst = useMemo(
    () => [...iterations].reverse(),
    [iterations],
  );
  const logosByScope = useMemo(() => {
    try {
      const logos = detail.logos ?? [];
      return {
        product: logos.filter((logo) => (logo.scope ?? "product") === "product"),
        order: logos.filter((logo) => logo.scope === "order"),
      };
    } catch (error) {
      console.error("Failed to group logos by scope:", error);
      return { product: [], order: [] };
    }
  }, [detail.logos]);
  const [openIteration, setOpenIteration] = useState<number | false>(false);

  useEffect(() => {
    try {
      const latest = iterations[iterations.length - 1];
      setOpenIteration(latest?.iteration ?? false);
    } catch (error) {
    }
  }, [detail.order_id, detail.product_id, iterations.length]);

  const daysHint =
    detail.days_to_sla == null
      ? undefined
      : detail.days_to_sla < 0
        ? `${Math.abs(detail.days_to_sla)}d overdue`
        : detail.days_to_sla === 0
          ? "Due today"
          : `${detail.days_to_sla}d left`;

  const meta: Array<[string, string]> = [
    ["Type", detail.order_type || "-"],
    ["Start", formatDate(detail.start_date)],
    ["Qty", String(detail.ordered_quantity)],
    ["Upload TAT", formatHours(detail.mockup_upload_tat_hours)],
    ["Approval TAT", formatHours(detail.mockup_approval_tat_hours)],
    ["Total TAT", formatHours(detail.total_tat_hours)],
    ["PM", detail.pm_poc || "Unassigned"],
    ["Sales", detail.sales_poc || "-"],
    ...(canViewDispatchDate && detail.dispatch_date
      ? ([["Dispatch", formatDate(detail.dispatch_date)]] as Array<[string, string]>)
      : []),
  ];

  return (
    <>
      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="Elapsed"
              value={formatHours(detail.sla_elapsed_hours)}
              hint={`Since ${formatDate(detail.sla_clock_start)}`}
              pill={{ label: sla.label, bg: sla.bg, fg: sla.fg }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow={
                isApprovalSlaClock(detail.mockup_work_state)
                  ? "Days to approval SLA"
                  : isReworkSlaClock(detail.mockup_work_state)
                    ? "Days to rework SLA"
                    : "Days to upload SLA"
              }
              value={
                detail.days_to_sla == null ? "-" : `${detail.days_to_sla}d`
              }
              hint={daysHint}
              pill={
                detail.mockup_work_state &&
                detail.mockup_work_state in MOCKUP_WORK_STATE_LABELS
                  ? {
                      label:
                        MOCKUP_WORK_STATE_LABELS[
                          detail.mockup_work_state as MockupWorkState
                        ],
                      bg: work.bg,
                      fg: work.fg,
                    }
                  : undefined
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <StatTile
              eyebrow="Upload SLA"
              value={formatSlaDays(detail.mockup_upload_sla_days)}
              hint={`${formatHours(detail.mockup_upload_tat_hours)} from project start`}
              pill={
                isUploadSlaClock(detail.mockup_work_state)
                  ? { label: "Now", bg: work.bg, fg: work.fg }
                  : undefined
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <StatTile
              eyebrow="Rework SLA"
              value={formatSlaDays(detail.mockup_rework_sla_days)}
              hint="From last change request. Shorter than first upload."
              pill={
                isReworkSlaClock(detail.mockup_work_state)
                  ? { label: "Now", bg: work.bg, fg: work.fg }
                  : undefined
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <StatTile
              eyebrow="Approval SLA"
              value={formatSlaDays(detail.mockup_approval_sla_days)}
              hint={
                detail.mockup_approval_tat_hours == null
                  ? "Starts after mockup upload"
                  : `${formatHours(detail.mockup_approval_tat_hours)} from mockup upload`
              }
              pill={
                isApprovalSlaClock(detail.mockup_work_state)
                  ? { label: "Now", bg: work.bg, fg: work.fg }
                  : undefined
              }
            />
          </Grid2>
        </Grid2>
        {detail.sla_delta_hours != null && detail.sla_delta_hours > 0 ? (
          <CustomTypography
            variant="caption"
            sx={{ color: colors.errorRed, display: "block", mt: 1.5 }}
          >
            Over target by {formatHours(detail.sla_delta_hours)}
          </CustomTypography>
        ) : null}
      </Paper>

      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <SectionCaption>Order</SectionCaption>
        <Grid2 container spacing={1.5} columns={12}>
          {meta.map(([label, value]) => (
            <Grid2 key={label} size={{ xs: 6, sm: 3 }}>
              <CustomTypography
                variant="caption"
                fontWeight={600}
                sx={{ color: pageColors.subtitle, display: "block" }}
              >
                {label}
              </CustomTypography>
              <CustomTypography
                variant="body2"
                fontWeight={500}
                sx={{ color: pageColors.title }}
              >
                {value}
              </CustomTypography>
            </Grid2>
          ))}
        </Grid2>
      </Paper>

      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <SectionCaption>Pipeline</SectionCaption>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {detail.pipeline.map((step) => (
            <Chip
              key={`${step.task}-${step.stage}`}
              size="small"
              label={pipelineLabel(step.task)}
              sx={dailyTasksChipSx(theme, step.complete ? "ok" : "neutral")}
            />
          ))}
        </Box>
      </Paper>

      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <SectionCaption>
          Client logos (
          {logosByScope.product.length + logosByScope.order.length})
        </SectionCaption>
        {logosByScope.product.length === 0 &&
          logosByScope.order.length === 0 ? (
          <DailyTasksEmptyState
            inPaper={false}
            icon={ImageOutlinedIcon}
            title="No logos yet"
            description="No logos on this product or this order."
          />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <LogoScopeBlock
              scope="product"
              emptyCopy="None on this product. Order-level logos below apply here."
              logos={logosByScope.product}
            />
            <LogoScopeBlock
              scope="order"
              emptyCopy="None shared across all products on this order."
              logos={logosByScope.order}
            />
          </Box>
        )}
      </Paper>

      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <SectionCaption>Mockups ({currentMockups.length})</SectionCaption>
        {currentMockups.length === 0 ? (
          <DailyTasksEmptyState
            inPaper={false}
            icon={BrushOutlinedIcon}
            title="No mockups yet"
            description="Uploaded mockups for this product will appear here."
          />
        ) : (
          <Grid2 container spacing={1.5} columns={12}>
            {currentMockups.map((mockup) => (
              <Grid2 key={mockup.id} size={{ xs: 12, sm: 6 }}>
                <MockupCard mockup={mockup} alt="mockup" />
              </Grid2>
            ))}
          </Grid2>
        )}
      </Paper>

      {iterations.length > 0 ? (
        <Box>
          <SectionCaption>
            Previous iterations ({iterations.length})
          </SectionCaption>
          {newestFirst.map((round) => {
            const tone = iterationTone(theme, round.status);
            return (
              <Accordion
                key={round.iteration}
                expanded={openIteration === round.iteration}
                onChange={(_, expanded) => {
                  try {
                    setOpenIteration(expanded ? round.iteration : false);
                  } catch (error) {
                  }
                }}
                disableGutters
                sx={{
                  mb: 1,
                  borderRadius: "8px !important",
                  border: `1px solid ${pageColors.border}`,
                  boxShadow: pageColors.shadowSoft,
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 1.5,
                    minHeight: 56,
                    "& .MuiAccordionSummary-content": {
                      my: 1,
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    },
                  }}
                >
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: pageColors.title }}
                  >
                    Iteration {round.iteration}
                  </CustomTypography>
                  <StatusPill
                    label={MOCKUP_ITERATION_STATUS_LABELS[round.status]}
                    bg={tone.bg}
                    fg={tone.fg}
                  />
                  <CustomTypography
                    variant="caption"
                    sx={{ color: pageColors.subtitle }}
                  >
                    {formatDate(round.started_at)}
                    {round.mockups.length > 1
                      ? ` · ${round.mockups.length} files`
                      : ""}
                  </CustomTypography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                  <Grid2 container spacing={1.5} columns={12}>
                    {round.mockups.map((mockup) => (
                      <Grid2
                        key={`${round.iteration}-${mockup.id}`}
                        size={{ xs: 12, sm: 6 }}
                      >
                        <MockupCard
                          mockup={mockup}
                          alt={`iteration ${round.iteration} mockup`}
                        />
                      </Grid2>
                    ))}
                  </Grid2>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ) : null}
    </>
  );
};

export default MockupDrawerBody;
